// store/modules/teams.ts
import {
  collection,
  getDocs,
  getCountFromServer,
  query,
  orderBy,
  limit as qLimit,
  startAfter,
  startAt,
  endAt,
  updateDoc,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
  getDoc,
  doc,
  addDoc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "@/firebaseConfig";
import type { ActionContext, Module } from "vuex";
import type { Team, TeamDoc } from "@/models/interfaces";

export interface TeamsState {
  fetching: { value: boolean };
  error: string;
  teams: { data: Team[] };
  total: number;
  byId: Record<string, { id: string; title: string }>;
  pageIndex: number;
  pageSize: number;
  cursors: Array<QueryDocumentSnapshot<DocumentData> | null>;
  nameSearch: string;
  accountId: number | null;
}

const initialState = (): TeamsState => ({
  fetching: { value: false },
  error: "",
  teams: { data: [] },
  byId: {} as Record<string, { id: string; title: string }>,
  total: 0,
  pageIndex: 0,
  pageSize: 10,
  cursors: [],
  nameSearch: "",
  accountId: null,
});

type RootState = unknown;

const teamsModule: Module<TeamsState, RootState> = {
  namespaced: true,

  state: initialState,

  getters: {
    isFetching: (state) => !!state.fetching.value,
    teams: (state) => state.teams.data,
    total: (state) => state.total,
    byId: (s: any) => s.byId,
    pageIndex: (state) => state.pageIndex,
    pageSize: (state) => state.pageSize,
    hasPrev: (state) => state.pageIndex > 0,
    hasNext: (state) => (state.pageIndex + 1) * state.pageSize < state.total,
  },

  mutations: {
    SET_FETCHING(state, v: unknown) {
      state.fetching.value = !!v;
    },
    SET_ERROR(state, msg?: string) {
      state.error = msg || "";
    },
    SET_TEAMS(state, rows: Team[]) {
      state.teams.data = Array.isArray(rows) ? rows : [];
    },
    SET_TOTAL(state, n: number) {
      state.total = Number(n) || 0;
    },
    SET_PAGE_INDEX(state, i: number) {
      state.pageIndex = Number(i) || 0;
    },
    SET_PAGE_SIZE(state, n: number) {
      state.pageSize = Number(n) || 10;
    },
    RESET_PAGINATION(state) {
      state.pageIndex = 0;
      state.cursors = [];
    },
    SET_CURSOR_FOR_INDEX(
      state,
      { index, snap }: { index: number; snap: QueryDocumentSnapshot | null }
    ) {
      state.cursors[index] = snap || null;
    },
    SET_NAME_SEARCH(state, term: string) {
      state.nameSearch = String(term || "").trim();
    },
    RESET_STATE(state) {
      Object.assign(state, initialState());
    },
    UPSERT_TEAMS(s: any, teams: Array<{ id: string; title: string }>) {
      for (const r of teams) s.byId[r.id] = r;
    },
    ADD_ITEM(s: TeamsState, role: Team) {
      // Keep local list sorted by title
      s.teams.data = [...s.teams.data, role].sort((a, b) =>
        a.title.localeCompare(b.title, undefined, { sensitivity: "base" })
      );
    },
    REMOVE_ITEM(s: TeamsState, id: string) {
      s.teams.data = s.teams.data.filter((r) => r.id !== id);
    },
    SET_ACCOUNT_ID(state, accountId: number | null) {
      state.accountId = accountId ?? null;
    },
    SET_HIDE_FROM_EVENTS(state, { id, value }: { id: string; value: boolean }) {
      const team = state.teams.data.find((t) => t.id === id);
      if (team) (team as any).hideFromEvents = value;
      if (state.byId[id]) (state.byId[id] as any).hideFromEvents = value;
    },
    SET_MAX_USERS_ON_EVENT(state, { id, value }: { id: string; value: number }) {
      const team = state.teams.data.find((t) => t.id === id);
      if (team) (team as any).maxUsersOnEvent = value;
      if (state.byId[id]) (state.byId[id] as any).maxUsersOnEvent = value;
    },
    SET_SELF_ASSIGNABLE_ON_EVENTS(
      state,
      { id, value }: { id: string; value: boolean }
    ) {
      const team = state.teams.data.find((t) => t.id === id);
      if (team) (team as any).selfAssignableOnEvents = value;
      if (state.byId[id])
        (state.byId[id] as any).selfAssignableOnEvents = value;
    },
  },

  actions: {
    async setAccount({ commit, dispatch }, accountId: number | null) {
      commit("SET_ACCOUNT_ID", accountId);
      // commit("RESET_PAGINATION");
      // await (dispatch as any)("initTotal");
      // return (dispatch as any)("loadPage", { index: 0 });
    },

    async fetchByIds(
      { commit }: ActionContext<TeamsState, RootState>,
      ids: string[]
    ) {
      const results: Array<{ id: string; title: string }> = [];
      for (const id of ids) {
        const r = await getDoc(doc(db, "teams", id));
        if (r.exists()) results.push({ id: r.id, title: r.data().title || "" });
      }
      commit("UPSERT_TEAMS", results);
    },

    async initTotal({ commit, state }) {
      commit("SET_ERROR", "");
      if (state.accountId == null) {
        commit("SET_TOTAL", 0);
        return;
      }

      const col = collection(db, "teams");
      const constraints: any[] = [where("account", "==", state.accountId)];

      // Optional prefix search on `title`
      const term = state.nameSearch;
      if (term) {
        constraints.push(orderBy("title"));
        constraints.push(startAt(term));
        constraints.push(endAt(term + "\uf8ff"));
      }

      const qRef = query(col, ...constraints);
      const countSnap = await getCountFromServer(qRef);
      commit("SET_TOTAL", countSnap.data().count);
    },

    setPageSize({ commit, dispatch }, size: number) {
      commit("SET_PAGE_SIZE", size);
      commit("RESET_PAGINATION");
      return (dispatch as any)("loadPage", { index: 0 });
    },

    setNameSearch({ commit, dispatch }, term: string) {
      commit("SET_NAME_SEARCH", term);
      commit("RESET_PAGINATION");
      return (dispatch as any)("loadPage", { index: 0 });
    },

    async loadPage(
      { state, commit, dispatch },
      { index = 0 }: { index?: number }
    ) {
      commit("SET_FETCHING", true);
      commit("SET_ERROR", "");

      try {
        if (state.accountId == null) {
          commit("SET_TEAMS", []);
          commit("SET_PAGE_INDEX", 0);
          return;
        }

        const colRef = collection(db, "teams");
        const constraints: any[] = [where("account", "==", state.accountId)];

        // Order by title for stable sort + nameSearch
        constraints.push(orderBy("title"));

        // Optional prefix search on title
        const term = state.nameSearch;
        if (term) {
          constraints.push(startAt(term));
          constraints.push(endAt(term + "\uf8ff"));
        }

        // Pagination
        constraints.push(qLimit(state.pageSize));
        if (index > 0) {
          const cursor = state.cursors[index - 1];
          if (cursor) constraints.push(startAfter(cursor));
        }

        const qRef = query(colRef, ...constraints);
        const snap = await getDocs(qRef);

        const rows: TeamDoc[] = snap.docs.map((d) => {
          const data = (d.data() || {}) as Partial<Team>;
          return {
            id: d.id,
            title: String(data.title ?? ""),
            account: Number(data.account ?? 0),
            users: Array.isArray(data.users) ? data.users : [],
            hideFromEvents: Boolean(data.hideFromEvents),
            selfAssignableOnEvents: Boolean(data.selfAssignableOnEvents),
            maxUsersOnEvent: Number(data.maxUsersOnEvent ?? 0),
          } as Team;
        });

        commit("SET_TEAMS", rows);
        commit("SET_CURSOR_FOR_INDEX", {
          index,
          snap: snap.docs.length
            ? snap.docs[snap.docs.length - 1]
            : state.cursors[index] ?? null,
        });
        commit("SET_PAGE_INDEX", index);

        // Keep total in sync when needed
        if (!state.total) {
          await (dispatch as any)("initTotal");
        }
      } catch (e: any) {
        commit("SET_ERROR", e?.message || String(e));
      } finally {
        commit("SET_FETCHING", false);
      }
    },

    async fetchAll({ state, commit }) {
      if (state.accountId == null) {
        console.warn("teams.fetchAll() has not accountId");
        commit("SET_TEAMS", []);
        return;
      }
      const qRef = query(
        collection(db, "teams"),
        where("account", "==", state.accountId),
        orderBy("title")
      );
      const snap = await getDocs(qRef);
      const rows: Team[] = snap.docs.map((d) => {
        const data = d.data() as Team;
        data.id = d.id;
        data.hideFromEvents = Boolean(data.hideFromEvents);
        data.selfAssignableOnEvents = Boolean(data.selfAssignableOnEvents);
        data.maxUsersOnEvent = Number(data.maxUsersOnEvent ?? 0);
        return data;
      });
      commit("SET_TEAMS", rows);
    },

    async nextPage({ getters, dispatch, state }) {
      if (!getters.hasNext) return;
      return (dispatch as any)("loadPage", { index: state.pageIndex + 1 });
    },

    async prevPage({ getters, dispatch, state }) {
      if (!getters.hasPrev) return;
      return (dispatch as any)("loadPage", { index: state.pageIndex - 1 });
    },

    async updateHideFromEvents(
      { commit },
      { teamId, value }: { teamId: string; value: boolean }
    ) {
      if (!teamId) throw new Error("teamId is required");
      await updateDoc(doc(db, "teams", teamId), { hideFromEvents: value });
      commit("SET_HIDE_FROM_EVENTS", { id: teamId, value });
    },

    async updateSelfAssignableOnEvents(
      { commit },
      { teamId, value }: { teamId: string; value: boolean }
    ) {
      if (!teamId) throw new Error("teamId is required");
      await updateDoc(doc(db, "teams", teamId), {
        selfAssignableOnEvents: value,
      });
      commit("SET_SELF_ASSIGNABLE_ON_EVENTS", { id: teamId, value });
    },

    async updateMaxUsersOnEvent(
      { commit },
      { teamId, value }: { teamId: string; value: number }
    ) {
      if (!teamId) throw new Error("teamId is required");
      await updateDoc(doc(db, "teams", teamId), { maxUsersOnEvent: value });
      commit("SET_MAX_USERS_ON_EVENT", { id: teamId, value });
    },

    async addTeam(
      { state, commit }: ActionContext<TeamsState, RootState>,
      payload: { title: string; createdBy: string }
    ) {
      if (state.accountId == null) throw new Error("No accountId set");
      const docData: TeamDoc = {
        account: state.accountId,
        createdBy: payload.createdBy,
        title: payload.title.trim(),
        hideFromEvents: false,
      };
      const ref = await addDoc(collection(db, "teams"), docData);
      // Optimistic add to local state
      commit("ADD_ITEM", { id: ref.id, ...docData });
      return ref.id;
    },

    async deleteRole(
      { commit }: ActionContext<TeamsState, RootState>,
      id: string
    ) {
      await deleteDoc(doc(db, "teams", id));
      commit("REMOVE_ITEM", id);
    },

    reset({ commit }) {
      commit("RESET_STATE");
    },
  },
};

export default teamsModule;
